import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Screen, { isScreenLinked } from '../../models/Screen';
import Option from "../../models/Option";
import Playlist, { getOption } from "../../models/Playlist";
import SelectAsyncPaginate from '../SelectAsyncPaginate';
import RenderUser from '../RenderUser';
import PlaylistService from '../../services/use_case/playlist/PlaylistService';

interface ScreenItemProps {
    screen: Screen;
    defaultOption: Option[];
    onChange?: (screen: Screen, option: Option) => void;
    visibleCheck?: boolean;
    isChecked?: boolean;
    onChecked?: (screen: Screen) => void;
};

const ScreenItem: React.FC<ScreenItemProps> = (props: ScreenItemProps) => {

    const [t] = useTranslation();
    const [value, setValue] = useState<Option | null>(null);

    let playlistService: PlaylistService | null = null;

    useEffect(() => {
        setValue( _getDefaultValue() );
    }, []);

    const _getDefaultValue = (): Option | null => {
        let playlist: Playlist | null = props.screen.linked ?? null;

        if(!playlist) return null;

        return getOption(playlist);

    }

    const _fetchPlaylists = async (search:string, prevOptions: any = null , { page }: { page: number }) => {
        try {

            playlistService = new PlaylistService();
            let response: any = await playlistService.fetch(page, search);
            const responseData = response.data?.data;

            let playlists: Playlist[] = response.data?.data?.data ?? [];

            let noOption: Option = {
                label: '-',
                value: null
            }
    
            let data = [
                noOption, 
                ...playlists.map((playlist: Playlist) => getOption(playlist) )
            ];

            return {
                options: data,
                hasMore: responseData?.last_page > page,
                additional: {
                    page: responseData.current_page + 1
                },
            }
        }
        catch(error) {
            return {
                options: [],
                hasMore: false,
                additional: {
                    page: 1
                }
            };
        }
    }

    const _getStatusVariant = (): 'configured' | 'unpaired' => {
        if (!isScreenLinked(props.screen)) return 'unpaired';
        return 'configured';
    }

    const _renderStatus = () => {
        const variant = _getStatusVariant();
        const labels = { configured: t('Configured'), unpaired: t('Unpaired') };
        return (
            <div className={`screen-item__status-badge screen-item__status-badge--${variant}`}>
                <span className="screen-item__status-dot" />
                <span className="screen-item__status-label">{ labels[variant] }</span>
            </div>
        );
    }

    const isChecked = props.isChecked ?? false;
    const batchActive = props.visibleCheck ?? false;

    const _handleLinkClick = (e: React.MouseEvent) => {
        if (batchActive) e.preventDefault();
    };

    return(
        <div
            className="card mb-3"
            style={batchActive ? { cursor: 'pointer' } : undefined}
            onClick={batchActive ? () => props.onChecked?.(props.screen) : undefined}
        >
            <div className={`card-body screen-item${isChecked ? ' checked' : ''}`}>
                <div className="screen-item__info">
                    <Link
                        to={`screens/${props.screen.id}`}
                        className="screen-item__name"
                        onClick={_handleLinkClick}
                    >
                        <i className="iconsminds-monitor"></i>
                        <span className="ml-2">{ props.screen.name }</span>
                    </Link>
                    { props.screen.tags && props.screen.tags.length > 0 && (
                        <div className="screen-item__tags">
                            { props.screen.tags.map((tag) => (
                                <span key={tag.id} className="screen-item__tag">{ tag.name }</span>
                            ))}
                        </div>
                    )}
                    {_renderStatus()}
                </div>
                <div
                    className="screen-item__playing"
                    onClick={batchActive ? (e) => e.stopPropagation() : undefined}
                >
                    <div className="screen-item__playing__title">
                        { t('Default content') }
                    </div>
                    <SelectAsyncPaginate
                        fetchOptions={_fetchPlaylists}
                        value={value}
                        onChange={(selectedOption: Option) => {
                            setValue(selectedOption);
                            props.onChange?.(props.screen, selectedOption);
                        }}
                    />
                </div>
                <div className="screen-item__actions">
                    <RenderUser item={props.screen} />
                    <Link
                        to={`screens/${props.screen.id}`}
                        className="screen-item__detail-link"
                        onClick={_handleLinkClick}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </Link>
                </div>
            </div>
        </div>
    );

};

export default React.memo(ScreenItem);
